import React, { useMemo, useState } from 'react';
import { Search, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { TableSkeleton } from './Skeleton';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';

export type Column<T> = {
  key: string;
  label: string;
  /** Render the cell. Defaults to `String(row[key])`. */
  render?: (row: T) => React.ReactNode;
  /** Sort accessor. If omitted column is not sortable. */
  sortValue?: (row: T) => string | number | null | undefined;
  /** Filter accessor. If omitted, falls back to render result text. */
  searchValue?: (row: T) => string | null | undefined;
  align?: 'left' | 'right';
  className?: string;
  thClassName?: string;
};

interface Props<T> {
  rows: T[];
  loading: boolean;
  error: string | null;
  columns: Column<T>[];
  rowKey: (row: T) => string | number;
  pageSize?: number;
  onRowClick?: (row: T) => void;
  searchPlaceholder?: string;
  emptyTitle?: string;
  emptyHint?: string;
  toolbar?: React.ReactNode;
  onRetry?: () => void;
  /** Hide the search input. Defaults to false. */
  hideSearch?: boolean;
  /** Selection support. When provided, a checkbox column is added. */
  selection?: {
    selected: Set<string | number>;
    onToggle: (key: string | number) => void;
    onToggleAll: (rowKeys: (string | number)[], allSelected: boolean) => void;
  };
}

/**
 * Reusable admin data table with client-side search, sort, pagination,
 * optional row-selection, and click-to-drill-down. Loading/error/empty
 * states are first-class — every list view in the admin console funnels
 * through this component for consistency.
 */
export function DataTable<T>({
  rows,
  loading,
  error,
  columns,
  rowKey,
  pageSize = 25,
  onRowClick,
  searchPlaceholder = 'Zoek…',
  emptyTitle,
  emptyHint,
  toolbar,
  onRetry,
  hideSearch = false,
  selection,
}: Props<T>) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      for (const col of columns) {
        const sv = col.searchValue ? col.searchValue(row) : null;
        if (sv && String(sv).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [rows, query, columns]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortValue) return filtered;
    const sorter = col.sortValue;
    const copy = [...filtered].sort((a, b) => {
      const av = sorter(a);
      const bv = sorter(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir, columns]);

  const total = sorted.length;
  const slice = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const toggleSort = (key: string) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null); setSortDir('asc');
  };

  const sliceKeys = slice.map(rowKey);
  const allOnPageSelected = selection ? sliceKeys.length > 0 && sliceKeys.every((k) => selection.selected.has(k)) : false;

  return (
    <div>
      {(!hideSearch || toolbar) && (
        <div className="px-4 md:px-5 py-3 border-b border-black/[0.06] dark:border-white/10 flex items-center justify-between gap-3 flex-wrap">
          {!hideSearch ? (
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="w-4 h-4 text-ink/40 dark:text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(0); }}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/15 hover:border-black/20 dark:hover:border-white/25 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 bg-white dark:bg-ink/40 dark:text-white placeholder:text-ink/40 dark:placeholder:text-white/40"
              />
            </div>
          ) : <span />}
          {toolbar}
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={6} cols={Math.min(columns.length, 6)} />
      ) : error ? (
        <ErrorState message={error} onRetry={onRetry} />
      ) : slice.length === 0 ? (
        <EmptyState title={emptyTitle ?? (query ? `Geen resultaten voor "${query}"` : 'Geen gegevens')} hint={emptyHint} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-ink/45 dark:text-white/45 bg-black/[0.02] dark:bg-white/[0.03]">
                  {selection && (
                    <th className="px-4 py-2.5 w-8">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={() => selection.onToggleAll(sliceKeys, allOnPageSelected)}
                        className="accent-accent"
                        aria-label="Selecteer pagina"
                      />
                    </th>
                  )}
                  {columns.map((col) => {
                    const sortable = !!col.sortValue;
                    const isActive = sortKey === col.key;
                    const ChevronIcon = !isActive ? ChevronsUpDown : sortDir === 'asc' ? ChevronUp : ChevronDown;
                    return (
                      <th
                        key={col.key}
                        className={`px-4 py-2.5 font-semibold ${col.align === 'right' ? 'text-right' : ''} ${col.thClassName ?? ''}`}
                      >
                        {sortable ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(col.key)}
                            className="inline-flex items-center gap-1 hover:text-ink dark:hover:text-white transition-colors"
                          >
                            {col.label}
                            <ChevronIcon size={11} className={isActive ? 'text-accent' : 'opacity-50'} />
                          </button>
                        ) : col.label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {slice.map((row) => {
                  const k = rowKey(row);
                  const selected = selection?.selected.has(k) ?? false;
                  return (
                    <tr
                      key={k}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      className={`border-t border-black/[0.04] dark:border-white/[0.06] transition-colors ${onRowClick ? 'cursor-pointer hover:bg-black/[0.015] dark:hover:bg-white/[0.04]' : ''} ${selected ? 'bg-accent/[0.05] dark:bg-accent/15' : ''}`}
                    >
                      {selection && (
                        <td className="px-4 py-2.5 w-8" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => selection.onToggle(k)}
                            className="accent-accent"
                            aria-label="Selecteer rij"
                          />
                        </td>
                      )}
                      {columns.map((col) => (
                        <td key={col.key} className={`px-4 py-2.5 ${col.align === 'right' ? 'text-right' : ''} ${col.className ?? ''}`}>
                          {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-4 md:px-5 py-3 border-t border-black/[0.06] dark:border-white/10 flex items-center justify-between text-sm text-ink/55 dark:text-white/55">
            <span>
              {total === 0 ? '0' : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, total)}`} van {total.toLocaleString('nl-NL')}
              {query && <span className="text-ink/35 dark:text-white/35"> (gefilterd)</span>}
            </span>
            <div className="flex gap-2">
              <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1.5 rounded-md border border-black/10 dark:border-white/15 hover:bg-black/[0.03] dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold text-ink/75 dark:text-white/75 transition-colors">
                Vorige
              </button>
              <button type="button" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * pageSize >= total}
                className="px-3 py-1.5 rounded-md border border-black/10 dark:border-white/15 hover:bg-black/[0.03] dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold text-ink/75 dark:text-white/75 transition-colors">
                Volgende
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
